import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentState {
  hasConsented: boolean;
  consentDate: string | null;
  preferences: CookiePreferences;
  acceptAll: () => void;
  rejectOptional: () => void;
  updatePreferences: (preferences: Partial<CookiePreferences>) => void;
  reset: () => void;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  essential: true, // 필수 쿠키는 항상 true
  analytics: false,
  marketing: false,
};

export const useCookieConsentStore = create<CookieConsentState>()(
  persist(
    (set) => ({
      hasConsented: false,
      consentDate: null,
      preferences: DEFAULT_PREFERENCES,

      // 모든 쿠키 허용
      acceptAll: () => {
        set({
          hasConsented: true,
          consentDate: new Date().toISOString(),
          preferences: {
            essential: true,
            analytics: true,
            marketing: true,
          },
        });
      },

      // 필수 쿠키만 허용 (추가 쿠키 거부)
      rejectOptional: () => {
        set({
          hasConsented: true,
          consentDate: new Date().toISOString(),
          preferences: {
            essential: true,
            analytics: false,
            marketing: false,
          },
        });
      },

      // 개별 쿠키 설정 업데이트
      updatePreferences: (newPreferences) => {
        set((state) => ({
          hasConsented: true,
          consentDate: new Date().toISOString(),
          preferences: {
            ...state.preferences,
            ...newPreferences,
            essential: true, // 필수 쿠키는 항상 true 유지
          },
        }));
      },

      // 동의 초기화 (테스트/개발용)
      reset: () => {
        set({
          hasConsented: false,
          consentDate: null,
          preferences: DEFAULT_PREFERENCES,
        });
      },
    }),
    {
      name: 'cookie-consent-storage',
      // 12개월(1년) 후 만료 확인
      version: 1,
    }
  )
);

// 동의 만료 확인 (12개월)
export const isConsentExpired = (consentDate: string | null): boolean => {
  if (!consentDate) return true;

  const consent = new Date(consentDate);
  const now = new Date();
  const monthsDiff =
    (now.getFullYear() - consent.getFullYear()) * 12 +
    (now.getMonth() - consent.getMonth());

  return monthsDiff >= 12;
};
